import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import ProductItem from '@components/ProductItem';
import Divider from '@material-ui/core/Divider';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import InputBase from '@material-ui/core/InputBase';
import { useTranslation } from 'next-i18next';
import ReactList from 'react-list';
import LazyLoading from 'react-list-lazy-load';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
  },
  categoryWrapper: {
    padding: '12px 12px 48px 12px',
    [theme.breakpoints.up('md')]: {
      padding: '30px 30px 90px 30px',
    },
  },
  dividerWrapper: {
    padding: '40px 12px 0px 12px',
    [theme.breakpoints.up('md')]: {
      padding: '30px 30px 10px 30px',
    },
  },
  divider: {
    margin: 0,
  },
  tabsRoot: {
    position: 'absolute',
    left: 0,
    backgroundColor: '#fff',
  },
  search: {
    position: 'relative',
    borderRadius: 10,
    backgroundColor: '#f6f7f9',
    '&:hover': {
      backgroundColor: '#eaebed',
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    marginTop: 20,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(3),
      width: 'auto',
    },
  },
  searchIcon: {
    cursor: 'pointer',
    zIndex: 88,
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    right: 0,
    top: 6,
  },
  inputRoot: {
    color: 'inherit',
    width: 'calc(100% - 60px)',
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 2),
    transition: theme.transitions.create('width'),
    width: '100%',
  },
  searchWrapper: {
    padding: '6px 12px',
    [theme.breakpoints.up('sm')]: {
      padding: '6px 0',
    },
  },
  contentWrapper: {
    padding: '6px 12px',
    [theme.breakpoints.up('sm')]: {
      padding: '6px 30px',
    },
  },
  productItemList: {
    '@global > div > div > div': {
      display: 'grid',
      gridTemplateColumns: '1fr',
      rowGap: 10,
      [theme.breakpoints.up('sm')]: {
        columnGap: 10,
        gridTemplateColumns: '1fr 1fr',
      },
    },
  },
}));

const ProductList = ({
  locale = 'en',
  categories = [],
  currentCategorySlug,
  keyword,
  setKeyword,
}) => {
  const classes = useStyles();
  const [totalResultFound, setTotalResultFound] = useState(0);
  const [finalCategories, setFinalCategories] = useState('');
  const { t } = useTranslation('common');
  const textInput = React.useRef(null);

  useEffect(() => {
    if (keyword) {
      handleSearch(keyword);
    } else {
      textInput.current.value = '';
      if (currentCategorySlug) {
        const _finalCategories = categories.filter(({ slug }) => slug === currentCategorySlug);
        setFinalCategories(_finalCategories);
      }
    }
  }, [keyword, categories, currentCategorySlug]);

  const handleSearch = _keyword => {
    if (categories.length === 0) return false;
    let newKeyword = _keyword.toLowerCase();
    let hasFound = false;
    let _categories = [];
    let _totalResultFound = 0;
    categories.forEach(({ products, ...data }) => {
      let _products = [];
      if (products.length > 0) {
        _products = products.filter(product => {
          if (product.name) {
            if (product.name.toLowerCase().search(newKeyword) > -1) {
              hasFound = true;
              _totalResultFound++;
              return true;
            }
          }
          return false;
        });
      }
      if (_products.length > 0) {
        _categories.push({
          ...data,
          products: _products,
        });
      }
    });
    setTotalResultFound(_totalResultFound);
    setFinalCategories(hasFound ? _categories : []);
    window.history.pushState(null, null, `${window.location.pathname}?q=${_keyword}`);
  };

  const onChangeSearch = e => {
    const _keyword = e.target.value?.trim();
    setKeyword(_keyword);
  };

  const onClickSearch = () => {
    setKeyword(keyword);
  };

  return (
    <div className={classes.root}>
      <div className={classes.searchWrapper}>
        <div className={classes.search}>
          <InputBase
            defaultValue={keyword}
            onBlur={onChangeSearch}
            inputRef={textInput}
            placeholder={t('search-in-store')}
            classes={{
              root: classes.inputRoot,
              input: classes.inputInput,
            }}
          />
          <div className={classes.searchIcon} onClick={onClickSearch} id="search-bar">
            <SearchIcon />
          </div>
        </div>
        {keyword && totalResultFound > 0 ? (
          <div className={classes.contentWrapper}>
            <p>
              {t('total-products-found', { keyword })}: {totalResultFound}
            </p>
          </div>
        ) : null}
      </div>
      {finalCategories.length > 0 ? (
        finalCategories.map(({ label = '', products = [], slug = '' }, j) => {
          return products.length > 0 ? (
            <section id={slug} key={j}>
              {j !== 0 ? (
                <div className={classes.dividerWrapper}>
                  <Divider className={classes.divider} margin={0} />
                </div>
              ) : null}
              <div className={classes.categoryWrapper}>
                <Typography paragraph>
                  <b>{label}</b>
                </Typography>
                <div className={classes.productItemList}>
                  <LazyLoading
                    length={products.length}
                    items={products}
                    className={classes.productItemList}
                  >
                    <ReactList
                      className={classes.productItemList}
                      type="uniform"
                      itemRenderer={(idx, key) => (
                        <div className={classes.productItemWrapper} key={key}>
                          <ProductItem
                            price={products[idx].price}
                            productImage={products[idx].image}
                            productName={products[idx].name}
                            description={products[idx].description}
                            slug={products[idx].slug}
                            locale={products[idx].locale}
                            isAvailable={products[idx].is_available}
                          />
                        </div>
                      )}
                      length={products.length}
                    />
                  </LazyLoading>
                </div>
              </div>
            </section>
          ) : null;
        })
      ) : keyword ? (
        <div className={classes.contentWrapper}>
          <p>{t('no-products-found-for', { keyword })}</p>
        </div>
      ) : null}
    </div>
  );
};

ProductList.propTypes = {
  locale: PropTypes.string,
  categories: PropTypes.array,
  currentCategorySlug: PropTypes.string,
  keyword: PropTypes.string,
  setKeyword: PropTypes.func,
};

export default ProductList;
